/**
 * Events Debug Overlay
 *
 * A small, fixed-position panel inside the preview iframe that streams every
 * canonical event the bus emits. Replaces GTM Preview Mode for verifying
 * tracking wiring during development / preview.
 *
 * Activation:
 *   - URL flag: ?vibe_events=1 (anywhere in the page URL)
 *   - localStorage flag: localStorage.setItem('vibesites:events-debug', '1')
 *
 * This overlay is intentionally lightweight (no design tokens, plain inline
 * styles) so it works on any rendered vibesite without depending on theme
 * styles.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { VibesiteEventName, VibesiteEventPayload } from '../events';
import { useVibesiteEvents } from './events-context';

const MAX_EVENTS_DISPLAYED = 20;
const FLAG_QUERY_PARAM = 'vibe_events';
const FLAG_STORAGE_KEY = 'vibesites:events-debug';

interface DisplayedEvent {
  id: number;
  name: VibesiteEventName;
  payload: VibesiteEventPayload;
  receivedAt: string;
}

function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const search = window.location.search || '';
    if (search.includes(`${FLAG_QUERY_PARAM}=1`)) return true;
    return window.localStorage?.getItem(FLAG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

const styles: Record<string, CSSProperties> = {
  panel: {
    position: 'fixed',
    bottom: 16,
    right: 16,
    zIndex: 2147483647,
    width: 360,
    maxHeight: 'min(70vh, 600px)',
    background: '#0f172a',
    color: '#e2e8f0',
    borderRadius: 8,
    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.45)',
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: 11,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '1px solid #1e293b',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 10px',
    borderBottom: '1px solid #1e293b',
    background: '#1e293b',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 600,
    color: '#a78bfa',
  },
  badge: {
    background: '#334155',
    color: '#cbd5e1',
    padding: '1px 6px',
    borderRadius: 999,
    fontSize: 10,
  },
  controls: {
    display: 'flex',
    gap: 4,
  },
  btn: {
    background: 'transparent',
    color: '#94a3b8',
    border: '1px solid #334155',
    borderRadius: 4,
    padding: '2px 6px',
    fontSize: 10,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  list: {
    overflowY: 'auto',
    padding: 0,
    margin: 0,
    listStyle: 'none',
    flex: 1,
  },
  item: {
    padding: '6px 10px',
    borderBottom: '1px solid #1e293b',
  },
  itemHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 6,
    marginBottom: 2,
  },
  name: {
    color: '#a78bfa',
    fontWeight: 600,
  },
  time: {
    color: '#64748b',
    fontSize: 10,
  },
  payload: {
    margin: 0,
    padding: 6,
    background: '#0b1120',
    borderRadius: 4,
    color: '#cbd5e1',
    fontSize: 10,
    overflow: 'auto',
    maxHeight: 140,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  empty: {
    padding: '16px 10px',
    color: '#64748b',
    fontStyle: 'italic',
    textAlign: 'center',
  },
};

/**
 * Renders a fixed-position debug overlay when the feature flag is active.
 * Returns null otherwise — safe to mount unconditionally.
 */
export function EventsDebugOverlay() {
  const { subscribe } = useVibesiteEvents();
  const [enabled, setEnabled] = useState<boolean>(() => isDebugEnabled());
  const [collapsed, setCollapsed] = useState(false);
  const [events, setEvents] = useState<DisplayedEvent[]>([]);
  const idRef = useRef(0);

  // Re-evaluate the flag on mount and whenever localStorage might change.
  useEffect(() => {
    setEnabled(isDebugEnabled());
    const onStorage = () => setEnabled(isDebugEnabled());
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const off = subscribe('*', (name, payload) => {
      idRef.current += 1;
      const entry: DisplayedEvent = {
        id: idRef.current,
        name,
        payload,
        receivedAt: new Date().toLocaleTimeString(),
      };
      setEvents((prev) => {
        const next = [entry, ...prev];
        return next.length > MAX_EVENTS_DISPLAYED ? next.slice(0, MAX_EVENTS_DISPLAYED) : next;
      });
    });
    return off;
  }, [enabled, subscribe]);

  if (!enabled) return null;

  const handleDisable = () => {
    try {
      window.localStorage?.removeItem(FLAG_STORAGE_KEY);
    } catch {
      // ignore
    }
    setEnabled(false);
  };

  return (
    <div style={styles.panel} role="region" aria-label="Vibesite events debug overlay">
      <div style={styles.header}>
        <div style={styles.title}>
          <span>vibesite.events</span>
          <span style={styles.badge}>{events.length}</span>
        </div>
        <div style={styles.controls}>
          <button type="button" style={styles.btn} onClick={() => setEvents([])}>
            Clear
          </button>
          <button
            type="button"
            style={styles.btn}
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? 'Expand' : 'Collapse'}
          </button>
          <button
            type="button"
            style={styles.btn}
            title="Disable overlay (clears localStorage flag)"
            onClick={handleDisable}
          >
            ✕
          </button>
        </div>
      </div>
      {!collapsed && (
        <ul style={styles.list}>
          {events.length === 0 && (
            <li style={styles.empty}>
              Waiting for events… click any tracked CTA to see it appear here.
            </li>
          )}
          {events.map((e) => (
            <li key={e.id} style={styles.item}>
              <div style={styles.itemHead}>
                <span style={styles.name}>{e.name}</span>
                <span style={styles.time}>{e.receivedAt}</span>
              </div>
              <pre style={styles.payload}>
                {JSON.stringify(e.payload, null, 2)}
              </pre>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default EventsDebugOverlay;
